import {
  ProductCartVariantInterface,
  VariantIdInterface as CartVariantIdInterface,
} from "@/types/cart";
import { VariantCombination } from "@/types/product";

/** Narrowing: is the array the "new" combination format? */
export function isVariantCombinationArray(
  v: unknown,
): v is VariantCombination[] {
  return (
    Array.isArray(v) && v.length > 0 && Array.isArray((v as any)[0]?.variant)
  );
}

/** Build grouped UI options (per group) from the combination list */
export function buildVariantGroupsFromCombinations(
  combinations: VariantCombination[],
  productId: number,
): ProductCartVariantInterface[] {
  const byGroup = new Map<number, ProductCartVariantInterface>();

  for (const combo of combinations) {
    for (const opt of combo.variant) {
      const groupKey = opt.groupId.id;

      if (!byGroup.has(groupKey)) {
        byGroup.set(groupKey, {
          id: groupKey,
          groupId: { id: groupKey, name: opt.groupId.name },
          productId,
          variantId: [],
        });
      }

      // De-duplicate options
      const entry = byGroup.get(groupKey)!;
      if (!entry.variantId.some((v) => v.variantId === opt.id)) {
        entry.variantId.push({
          id: opt.id,
          value: opt.value,
          variantId: opt.id, // keep same as id for backward compatibility
        } as CartVariantIdInterface);
      }
    }
  }

  // Sort options for stable UI
  for (const [, group] of byGroup) {
    group.variantId.sort((a, b) => `${a.value}`.localeCompare(`${b.value}`));
  }
  return Array.from(byGroup.values()).sort(
    (a, b) => a.groupId.id - b.groupId.id,
  );
}

/** Default selection: take the first combination's options */
export function getDefaultSelectionFromCombinations(
  combinations: VariantCombination[],
  productId: number,
): ProductCartVariantInterface[] {
  if (!combinations.length) return [];
  const first = combinations[0];
  const grouped = new Map<number, ProductCartVariantInterface>();

  for (const opt of first.variant) {
    grouped.set(opt.groupId.id, {
      id: opt.groupId.id,
      groupId: { id: opt.groupId.id, name: opt.groupId.name },
      productId,
      variantId: [{ id: opt.id, value: opt.value, variantId: opt.id }],
    });
  }
  return Array.from(grouped.values()).sort(
    (a, b) => a.groupId.id - b.groupId.id,
  );
}

/** Compute allowed option ids for a target group given a partial selection */
export function getAllowedOptionIdsForGroup(
  combinations: VariantCombination[],
  selected: ProductCartVariantInterface[],
  targetGroupId: number,
): Set<number> {
  // Build a map of current selection excluding the target group
  const chosen = new Map<number, number>(); // groupId -> optionId
  for (const s of selected) {
    if (s.groupId.id !== targetGroupId && s.variantId?.[0]) {
      chosen.set(s.groupId.id, s.variantId[0].variantId);
    }
  }

  // Allowed = any option that appears in a combination consistent with the chosen map
  const allowed = new Set<number>();
  for (const combo of combinations) {
    // Check compatibility
    let ok = true;
    for (const [gId, optId] of chosen) {
      const hit = combo.variant.find(
        (v) => v.groupId.id === gId && v.id === optId,
      );
      if (!hit) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;

    // Collect all options for the target group in those compatible combos
    for (const v of combo.variant) {
      if (v.groupId.id === targetGroupId) allowed.add(v.id);
    }
  }
  return allowed;
}

/** Find exact price for a fully selected set (all groups chosen). Returns undefined if not matched yet */
export function getPriceForSelection(
  combinations: VariantCombination[],
  selected: ProductCartVariantInterface[],
): { price: number; combinationIds: number[] } | undefined {
  // If not fully chosen, no exact price yet
  if (selected.some((g) => !g.variantId?.[0])) return undefined;

  // Turn selection into a set of option ids
  const selectionIds = new Set(selected.map((g) => g.variantId[0].variantId));

  // Look for a combination whose options match exactly
  for (const combo of combinations) {
    const comboIds = new Set(combo.variant.map((v) => v.id));
    if (comboIds.size === selectionIds.size) {
      // Exact match if every selected id is in combo
      let match = true;
      for (const id of selectionIds) {
        if (!comboIds.has(id)) {
          match = false;
          break;
        }
      }
      if (match) {
        return { price: parseFloat(combo.price), combinationIds: combo.id };
      }
    }
  }
  return undefined;
}

/** Show min-max range when selection isn't complete */
export function getPriceRange(combinations: VariantCombination[]): {
  min: number;
  max: number;
} {
  let min = Number.POSITIVE_INFINITY;
  let max = 0;
  for (const c of combinations) {
    const p = parseFloat(c.price);
    if (!Number.isNaN(p)) {
      min = Math.min(min, p);
      max = Math.max(max, p);
    }
  }
  if (!isFinite(min)) min = 0;
  return { min, max };
}

/** Safer equality for cart variant selections (order-independent) */
export function areVariantSelectionsEqual(
  a: ProductCartVariantInterface[] = [],
  b: ProductCartVariantInterface[] = [],
): boolean {
  if (a.length !== b.length) return false;
  const mapA = new Map(
    a.map((x) => [x.groupId.id, x.variantId?.[0]?.variantId]),
  );
  const mapB = new Map(
    b.map((x) => [x.groupId.id, x.variantId?.[0]?.variantId]),
  );
  if (mapA.size !== mapB.size) return false;
  for (const [gid, optId] of mapA) {
    if (mapB.get(gid) !== optId) return false;
  }
  return true;
}
